import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import usePdfViewerStyles from 'core/lite/hooks/usePdfViewerStyles';
import useProgramBrandColors from 'core/lite/hooks/useProgramBrandColors';
import { useUser } from 'core/lite/hooks/useUser';
import { selectors as coursesSelectors } from 'core/store/courses.duck';
import { getGuidCourseProgram } from 'core/utils/course';
import { buildPdfJsToolbar, DEFAULT_VIEWER_TOOLBAR } from 'core/utils/pdfJsToolbar';
import { IS_INFANTIL_VIEWER_SKIN_DISABLED, resolveViewerSkinLevel, resolveViewerSkinRole } from 'core/utils/viewerSkin';

import type { CourseAPIResponse } from 'app/types/courses';
import type { UsePdfViewerStylesResult } from 'core/lite/hooks/usePdfViewerStyles';
import type { UseProgramBrandColorsResult } from 'core/lite/hooks/useProgramBrandColors';
import type { ViewerSkinLevel, ViewerSkinRole } from 'core/utils/viewerSkin';

type Toolbar = string[] | null | undefined;

type UsePdfJsViewerConfigParams = {
  isEnabled: boolean;
  toolbar?: Toolbar;
};

export type PdfJsSkinConfig = {
  brandColors: UseProgramBrandColorsResult['brandColors'];
  level: ViewerSkinLevel;
  role: ViewerSkinRole;
};

export type UsePdfJsViewerConfigResult = {
  customHotspotsImages: UsePdfViewerStylesResult['customHotspotsImages'];
  isReady: boolean;
  skinConfig: PdfJsSkinConfig | undefined;
  toolbar: Toolbar;
};

type ReadyConfig = Pick<UsePdfJsViewerConfigResult, 'customHotspotsImages' | 'skinConfig'>;

type ViewerCourse = Partial<Pick<CourseAPIResponse, 'education_level_guid' | 'education_level_name' | 'school_group_education_level_guid'>>;

function resolveLevel(course: ViewerCourse | null | undefined): ViewerSkinLevel {
  if (IS_INFANTIL_VIEWER_SKIN_DISABLED) return 'ESO';

  return resolveViewerSkinLevel(course?.education_level_guid || course?.school_group_education_level_guid, course?.education_level_name);
}

/** Desactivado no pide nada al API y devuelve la toolbar intacta; una vez listo, no vuelve a «no listo» al cambiar de programa. */
export default function usePdfJsViewerConfig({ isEnabled, toolbar }: UsePdfJsViewerConfigParams): UsePdfJsViewerConfigResult {
  const currentCourse: CourseAPIResponse | null | undefined = useSelector(coursesSelectors.getCurrentCourse);
  const { isStudent } = useUser();
  // Los estilos de hotspot y la Apariencia se configuran por programa, no por curso ni por contenido.
  const programGuid: string | undefined = isEnabled && currentCourse ? getGuidCourseProgram(currentCourse) : undefined;
  const { customHotspotsImages, isReady: areHotspotStylesReady } = usePdfViewerStyles(programGuid);
  const { brandColors, isReady: areBrandColorsReady } = useProgramBrandColors(programGuid);

  const level = resolveLevel(currentCourse);
  const role = resolveViewerSkinRole(isStudent);

  // Son props del `Viewer`: una referencia nueva en cada render reconfiguraría el motor.
  const skinConfig = useMemo(() => (isEnabled ? { level, role, brandColors } : undefined), [isEnabled, level, role, brandColors]);
  const isDefaultToolbar = toolbar === undefined || toolbar === null;
  const viewerToolbar = isDefaultToolbar ? DEFAULT_VIEWER_TOOLBAR : toolbar;
  const pdfJsToolbar = useMemo(
    () => (isEnabled ? buildPdfJsToolbar({ toolbar: viewerToolbar, isDefaultToolbar, level, role }) : viewerToolbar),
    [isEnabled, viewerToolbar, isDefaultToolbar, level, role]
  );

  // Se conserva la última configuración lista: si el programa cambia con el visor montado, volver a «no listo» lo desmontaría.
  const [lastReadyConfig, setLastReadyConfig] = useState<ReadyConfig | undefined>(undefined);
  const isCurrentConfigReady = areHotspotStylesReady && areBrandColorsReady;
  let readyConfig: ReadyConfig | undefined = isEnabled ? lastReadyConfig : undefined;
  if (isEnabled && isCurrentConfigReady && (lastReadyConfig?.skinConfig !== skinConfig || lastReadyConfig?.customHotspotsImages !== customHotspotsImages)) {
    readyConfig = { customHotspotsImages, skinConfig };
  }
  if (readyConfig !== lastReadyConfig) setLastReadyConfig(readyConfig);

  return {
    customHotspotsImages: readyConfig?.customHotspotsImages,
    isReady: !isEnabled || readyConfig !== undefined,
    skinConfig: readyConfig ? readyConfig.skinConfig : skinConfig,
    toolbar: pdfJsToolbar,
  };
}
